// 3.4.11  The Deal tag
// --------------------
//   The Deal tag value gives the cards of each hand.  The tag value is
// defined as "<first>:<1st_hand> <2nd_hand> <3rd_hand> <4th_hand>".  The 4
// hands are given in clockwise rotation.  A space character exists between
// two consecutive hands.  The direction of the <1st_hand> is indicated by
// <first>, being W (West), N (North), E (East), or S (South).  The cards of
// each hand are given in the order:  spades, hearts, diamonds, clubs.  A dot
// character "." exists between two consecutive suits of a hand.  The cards of
// a suit are given by their ranks.  The ranks are defined as (in descending
// order):
//     A , K , Q , J , T , 9 , 8 , 7 , 6 , 5 , 4 , 3 , 2.
//   Note that the 'ten' is defined as the single character "T". If a hand
// contains a void in a certain suit, then no ranks are entered at the place
// of that suit.
//   Not all 4 hands need to be given. A hand whose cards are not given, is
// indicated by "-" . For example, only the east/west hands are given:
// [Deal "W:KQT2.AT.J6542.85 - A8654.KQ5.T.QJT6 -"]
//   In import format, the ranks of a suit can be given in any order; the
// value of <first> is free.  In export format, the ranks must be given in
// descending order; <first> is equal to the dealer.

// Example
// [Event "Weak 2s"]
// [Site ""]
// [Date "2017.02.18"]
// [Board "1"]
// [West ""]
// [North ""]
// [East ""]
// [South ""]
// [Dealer "N"]
// [Vulnerable "None"]
// [Deal "N:KQT.AT9.J763.Q54 96532.765.542.K7 7.KQJ432.Q8.T982 AJ84.8.AKT9.AJ63"]
// [Declarer "E"]
// [Contract "3S"]
// [Result ""]
// [Auction "N"]
// 2H X  3H P
// P  X  P  3S
// P  P  P
//
//

const Game = require('./Game');
const Card = require('./models/Card');

// const deal = 'N:KQT.AT9.J763.Q54 96532.765.542.K7 7.KQJ432.Q8.T982 AJ84.8.AKT9.AJ63';
// const deal =  "W:KQT.AT9.J763.Q54 96532.765.542.K7 7.KQJ432.Q8.T982 AJ84.8.AKT9.AJ63";
// const deal = 'W:KQT.AT9.J763.Q54 - 7.KQJ432.Q8.T982 -';

const SUIT_NAMES = { S: 'Spades', H: 'Hearts', D: 'Diamonds', C: 'Clubs' };
const SUITS = ['S', 'H', 'D', 'C'];

const ORDER = {
	N: ['N', 'E', 'S', 'W'],
	E: ['E', 'S', 'W', 'N'],
	S: ['S', 'W', 'N', 'E'],
	W: ['W', 'N', 'E', 'S'],
};

/**
 * a PBN Deal Tag string
 *  </pre></pre>
 *  "W:KQT.AT9.J763.Q54 96532.765.542.K7 7.KQJ432.Q8.T982 AJ84.8.AKT9.AJ63"`
 * @param  {Object} pbnData in standard PBN format e.g.
 *  Takes an object with `deal`,  `auction` and `contract` tags, as derived
 *  <pre><code>
 *  {
 *  	deal: "N:KQT.AT9.J763.Q54 96532.765.542.K7 7.KQJ432.Q8.T982 AJ84.8.AKT9.AJ63",
 *   	auction: {
 *   	value: "N",
 *   	bids:
 *   	[
 *			"2H",
 *			"X",
 *			"3H",
 *			"P",
 *			"P",
 *			"X",
 *			"P",
 *			"3S",
 *			"P",
 *			"P",
 *		  "P"
 *    ]
 *   contract: "3S"
 *  }
 *
 */
class DealParser {
	constructor(pbnData) {
		this._deal = {
			dealer: null,
			players: {
				N: {
					hand: [],
				},
				E: {
					hand: [],
				},
				S: {
					hand: [],
				},
				W: {
					hand: [],
				},
			},
			auction: [], // TODO
			contract: null, // TODO
			vulnerable: null,
		};

		this._deck = [];

		this.actions = [];

		this.parseDeal(pbnData.deal);
		this.parseAuction(pbnData.auction || {});
		this.setContract(pbnData.contract || null);
		this.setVulnerable(pbnData.vulnerable || null);

		if (pbnData.actions) {
			this.actions = pbnData.actions;
		}
	}

	parseDeal(deal) {
		// FIRST is the first player to be dealt cards, meaning the player to FIRSTs right is the dealer.
		let [first, tail] = deal.split(':');

		this._deal.dealer = first;

		let playerOrder = ORDER[first];

		let hands = tail.split(' ');

		hands.forEach((hand, idx) => {
			let cards = [];
			let player = playerOrder[idx];
			if (hand != '-') {
				cards = hand.split('.');
				for (let suit in cards) {
					for (let card of cards[suit]) {
						let cardString = `${card}${SUITS[suit]}`;
						this._deal.players[player].hand.push(cardString);
						this._deck.push(cardString);
					}
				}
			}
		});
	}

	parseAuction(auction) {
		this._deal.auction = auction.bids || []; // simply a list of bids
	}

	setContract(contract) {
		this._deal.contract = contract;
	}
	setVulnerable(state) {
		this._deal.vulnerable = state;
	}

	/**
	 * The deal represented as an object
	 * <pre>
	 *   <code>
	 *   {
	 *     dealer: "N",
	 *     players: {
	 *      N: {
	 *        hand: ["2C", ...],
	 *      },
	 *      E: {
	 *        hand: ["3C", ...],
	 *      },
	 *      S: {
	 *        hand: ["4C", ...],
	 *      },
	 *      W: {
	 *        hand: ["5C", ...],
	 *      },
	 *  },
	 *  auction: [], // TODO
	 *  contract: "2NT", // TODO
	 *};
	 *   </code>
	 * </pre>
	 * @return {Object}
	 */
	get deal() {
		return this._deal;
	}

	/**
	 * The deck of cards used in this deal.
	 * e.g. `["2C", "TH", ...]`
	 * @return {String[]} an array of strings representing cards
	 */
	get deck() {
		return this._deck;
	}

	/**
	 * The dealer for this deal
	 * @return {String} e.g. `"N"`
	 */
	get dealer() {
		return this._deal.dealer;
	}

	/**
	 * The list of auction bids  e.g. ["P", "1NT", "P", "3NT", "X", "XX", "P", "P", "P"]
	 * @return {String} one of `P, {n}{suit|NT}, X, XX`,
	 */
	get auction() {
		return this._deal.auction;
	}

	/**
	 * The contract for this game e.g. `"3NT"`
	 * @return {String} the value of the contract
	 */
	get contract() {
		return this._deal.contract;
	}

	get vulnerable() {
		return this._deal.vulnerable;
	}
}

/**
 * Given a stateObject, returns a game instance
 * @param  {Object} stateObject the gameState object from GameState.state
 * @return {Game}
 */
DealParser.fromState = (stateObject) => {
	const Game = require('./Game');
	const parser = new DealParser(stateObject);

	// don't apply DDS until the last card is played
	const useDDS = false;
	let game = new Game(
		stateObject.name || null,
		stateObject.board || null,
		useDDS
	);

	game.createPlayers();
	game.createDeck();

	if (parser.vulnerable) {
		game.setVulnerable(parser.vulnerable);
	}

	game.setDealer(game.playerBySeat(parser.deal.dealer));

	['N', 'E', 'S', 'W'].forEach((seat) => {
		parser.deal.players[seat].hand.forEach((c) => {
			// let card = Card.fromString(c);
			// game.deck.addCard(card);

			game.dealCard(game.deck.card(c), game.playerBySeat(seat));
		});
	});

	if (parser.auction.length > 0) {
		game.addBids(parser.auction);
	}

	for (let card of parser.actions) {
		game.playCard(game.deck.card(card));
	}

	// don't apply DDS until the last card is played
	// enable dds and calculate scores
	if (stateObject.useDDS) {
		game.enableDDS();
		game.nextPlays();
	}

	return game;
};

/**
 * Given a gameDataObkect, returns a game instance
 * @param  {Object} gameDataObject the gameDataObject object from Game.currentGameData
 * @return {Game}
 */

DealParser.fromCurrentGameData = (gameDataObject) => {
	// convert back a stateObject
	let stateObject = {
		name: gameDataObject.name,
		board: gameDataObject.board,
		auction: {
			bids: gameDataObject.auction.bids.map((bid) => bid.bid), // ['3S', 'P', 'P', 'P']
			value: gameDataObject.auction.declarer, // "S"
		},
		contract: gameDataObject.contract,
		deal: gameDataObject.dealString,
		// 'S:26TA.48Q.37J.59K 37J.59K.48Q.26TA 48Q.26TA.59K.37J 59K.37J.26TA.48Q',
		vulnerable: gameDataObject.vulnerable,
		actions: gameDataObject.actions || [],
		useDDS: gameDataObject.useDDS,
	};

	return DealParser.fromState(stateObject);
};

/**
 * Given a standard PBN `deal` tag String, sets up the game with players with dealt hands, and sets the dealer
 * @param {Object} dealObject - in the form
 * ```
 * {
 * 	deal: "W:KQT.AT9.J763.Q54 96532.765.542.K7 7.KQJ432.Q8.T982 AJ84.8.AKT9.AJ63",
 * 	auction: {
 * 		value: "W",
 * 		bids: ["P", "3NT", "P", "P", "P"]
 * 	},
 * 	contract: "3NT"
 * }
 * ```
 * @return {Game} a pre-configured game
 */
DealParser.fromPBNObject = (
	dealObject,
	gameName = '',
	contractStr = undefined
) => {
	const parser = new DealParser(dealObject);

	const Game = require('./Game');

	let game = new Game(gameName, dealObject.board || null);

	game.createPlayers();
	game.setDealer(game.playerBySeat(parser.deal.dealer));
	if (contractStr) {
		parser.setContract(contractStr);
	}

	['N', 'E', 'S', 'W'].forEach((seat) => {
		parser.deal.players[seat].hand.forEach((c) => {
			let card = Card.fromString(c);
			game.deck.addCard(card);
			game.dealCard(card, game.playerBySeat(seat));
		});
	});

	if (parser.auction.length > 0) {
		game.addBids(parser.auction);
	}

	if (parser.contract) {
		game.setContract(parser.contract);
	}
	if (parser.vulnerable) {
		game.setVulnerable(parser.vulnerable);
	}

	return game;
};

/**
 * Creates a game, based on the contents of a PBN file.
 * Any file with a `[Deal "N:xxxxx"]` tag in will work. optionally parses the `[Contract "XXX"]` and `[Event "xxxx"]` tags also.
 * @param  {String} fileData - this should be the text content if a file, with at least a deal tag: `[Deal "D:CCC.CCC.CCC CCCC.CCC.CCC ..."]`
 * @param  {Object} fileMeta this is an optional object with a `name` property, that has been created by the browser file input
 * @return {Game}  game returns a game instance
 */
DealParser.fromPBNFileGameString = (fileData, fileMeta) => {
	let dealRE = /\[Deal "(.*)"\]/gm;
	let contractRE = /\[Contract "(.*)"\]/gm;
	let eventRE = /\[Event "(.*)"\]/gm;
	let boardRE = /\[Board "(.*)"\]/gm;
	let vulnerableRE = /\[Vulnerable "(.*)"\]/gm;
	let auctionRE = /\[Auction "([NSEW]*)"\]/gm;

	const dealMatches = dealRE.exec(fileData);

	let dealStr;
	if (!dealMatches) {
		throw new Error('Missing Deal tag in file data');
	}

	dealStr = dealMatches[1]; // match

	const contrMatches = contractRE.exec(fileData);

	let contrStr;
	if (contrMatches) {
		contrStr = contrMatches[1]; // match
	}

	const evMatches = eventRE.exec(fileData);
	let eventName;
	if (evMatches) {
		eventName = evMatches[1]; // match
	}

	const boardMatches = boardRE.exec(fileData);
	let board;
	if (boardMatches) {
		// check it has an integer value
		let _num = Number(boardMatches[1]);
		if (Number.isInteger(_num)) {
			board = _num; // match
		}
	}

	const vulnMatches = vulnerableRE.exec(fileData);
	let vulnerableState;
	if (vulnMatches) {
		vulnerableState = vulnMatches[1]; // match
	}

	const auctionMatches = auctionRE.exec(fileData);
	let auction;
	if (auctionMatches) {
		// console.log('Auction Matches', auctionMatches);
		auction = DealParser.parseAuctionTag(fileData);
	}
	//

	//
	let pbnConf = {
		name: eventName,
		board: board,
		deal: dealStr,
		contract: contrStr,
		auction: auction,
		vulnerable: vulnerableState,
	};

	return DealParser.fromPBNObject(pbnConf, eventName);
};

/*
Before describing the complete parsing process, the main syntax
elements of the PBN standard are repeated shortly.

1. A PBN game ends, when a semi-empty line is encountered. This is an
   empty line, or a line that only contains spaces and/or tabs. In the
   latter case, it looks as if the line is empty, so that it counts as
   an empty line. Of course, a game also ends at the end of the file.
2. The syntax of a tag is: [ <tag name> <tag value> ]
   where <tag name> is the name of tag, and <tag value> is a string
   starting and ending with a quote '"'.
3. Characters after the closing ']' of a tag are considered as section
   data. Processing of these data depends on the preceding tag. In PBN
   1.0 , the tags Auction and Play have an extensive syntax for their
   section data. In PBN 2.0 , a special syntax is defined for Note tags
   and several table tags. When you are not interested in section data,
   then just skip them. Just search for the '[' character, that means
   the start of the next tag. This remark is very important from a
   compatibility point of view.
4. Inside a string \" indicates a quote, and \\ indicates a (single)
   backslash.
5. Comments are given between the brackets pair '{' and '}' . Comment
   till the end-of-line starts with ';'.
6.  A line starting with '%' is an escape line.  It can be ignored,
    unless you are interested in its contents, e.g. the PBN version.

 */

/**
 * Creates a game, based on the contents of a PBN file.
 * Any file with a `[Deal "N:xxxxx"]` tag in will work. optionally parses the `[Contract "XXX"]` and `[Event "xxxx"]` tags also.
 * @param  {String} fileData - this should be the text content if a file, with at least a deal tag: `[Deal "D:CCC.CCC.CCC CCCC.CCC.CCC ..."]`
 * @param  {Object} fileMeta this is an optional object with a `name` property, that has been created by the browser file input
 * @return {Game}  game returns a game instance
 */
DealParser.fromPBNFileContent = (fileData, fileMeta, errorParts = []) => {
	// GAMES ARE SEPERATED BY EMPTY LINES...
	fileData = DealParser.fixLineEndings(fileData); // converts `\r\n` to `\n`
	let blankLineSeperatorRE = /(^\s*$)/gm;
	const parts = fileData.split(blankLineSeperatorRE);

	let gameParts = [];
	for (let part of parts) {
		if (part.trim() != '') {
			gameParts.push(part);
		}
	}

	let games = [];

	for (let gamePart of gameParts) {
		try {
			games.push(DealParser.fromPBNFileGameString(gamePart, fileMeta));
		} catch (err) {
			errorParts.push(gamePart);
			console.error('Unable to parse PBN file part, skipping');
			console.error(err);
		}
	}

	return games;
};

/**
 * Creates a game from a simple deal string, also accepts a metadata object with `name`, 'board', `contrStr`, `auction[]`, `vulnerableState`
 * @param  {String} dealString
 * @param  {Object} metaData
 * @return {Game}
 */
DealParser.fromDealString = (dealString, metaData = {}) => {
	const pbnConf = {
		name: metaData.name || null,
		board: metaData.board || null,
		deal: dealString || null,
		contract: metaData.contrStr || null,
		auction: metaData.auction || null,
		vulnerable: metaData.vulnerableState || null,
	};
	return DealParser.fromPBNObject(pbnConf);
};

/**
 * Converts line endings of the file data to `\n`
 * @param  {string} fileContent
 * @return {string} file content with replaced line endings
 */
DealParser.fixLineEndings = (fileContent) => {
	const parts = fileContent.split(/\r\n|\n|\r/g);
	const newContent = parts.join('\n');
	return newContent;
};

// 3.5  Auction section
// --------------------
//   The auction section gives the calls, together with the optional
// annotation.  The start of the auction section is indicated by the tag pair
// with tag name 'Auction'.  The Auction tag is followed by multiple auction
// lines containing the calls.  The direction of the player making the first
// call is given as tag value in the Auction tag pair:  "W" (West), "N"
// (North), "E" (East), or "S" (South).
//   In export format this player is always the dealer.  Each auction line
// contains 4 calls.  Only due to much annotation, a line may contain less
// than 4 calls.  The call of the dealer always starts left justified at a new
// line.
//   In import format the player in the Auction tag value need not be the
// dealer.  For example, the calls are given in a table of 4 columns with West
// in the first column.  In that case, each player before the dealer has a
// hyphen ("-") in the first auction line.
//   The auction section ends with all passes, or with "*" .  The "*"
// character indicates that no further calls will or can be given.  In this
// case, the result of the auction can be given in the tags 'Declarer' and
// 'Contract'.
//   It is also possible that the auction has not been completed yet, with the
// intention to continue another time.  For example, a game might be saved in
// a PBN file so that another computer program might make the next call.  The
// '+' character is used to indicate explicitly this intention.  The '+'
// replaces the next call to be made. Hence, it is placed after the last
// call (and its annotations).  At the continuation, the '+' will be replaced
// by the made call, and possibly a new '+' can be inserted. In this situation,
// the '*' character is forbidden to end the auction section.  The usage of '+'
// is not obligatory, only recommended.

// 3.5.1  Auction call
// -------------------
//   A call is represented by a call token.  The possibilities of a call token
// are:
//     AP                 all players pass
//     Pass               the player passes
//     X                  the player doubles
//     XX                 the player redoubles
//     <k><denomination>  the player bids a contract, where <k> and
//                        <denomination> are defined as in the Contract tag
//     -                  it is not yet player's turn to make a call

/**
 * Takes a PBN file contents, and parses the Auction tag into an object, or returns false
 * @example
 * fileData = `
 * %HtmlSpades entity,"http://bridgecomposer.com/suitimg/s.gif"
 * %HtmlHearts entity,"http://bridgecomposer.com/suitimg/h.gif"
 * %HtmlDiamonds entity,"http://bridgecomposer.com/suitimg/d.gif"
 * %HtmlClubs entity,"http://bridgecomposer.com/suitimg/c.gif"
 * [Site ""]
 * [Date "2017.02.18"]
 * [Deal "N:KQT.AT9.J763.Q54 96532.765.542.K7 7.KQJ432.Q8.T982 AJ84.8.AKT9.AJ63"]
 * [Board "1"]
 * [Auction "N"]
 * 2H X  3H P
 * P  X  P  3S
 * P  P  P
 * [Application "Bridge128"]`;
 *
 * let auction = DealParser.parseAuction(fileData);
 * console.log(auction);
 * {
 *  value: 'N',
 *  bids: ['2H', 'X', '3H', 'P', 'P', 'X', 'P', '3S', 'P', 'P', 'P'],
 * };
 *
 * @param  {String} fileData the contents of a PBN file
 * @return {Object} the object representing the Auction - ``` { value: "W", bids: ["P", "1NT", ...]} ```
 */
DealParser.parseAuctionTag = (fileData) => {
	let auctionRE = /\[Auction "([NSEW]*)"\]((\n\r*[\dXPCDHSNTAPPass ]*)*)/gm;
	const matches = auctionRE.exec(fileData);
	let bids = matches[2].split(/[\n\r ]/g).filter((match) => match != '');

	const normalizedbids = [];
	bids.map((b) => {
		if (b.slice(1) == 'N') {
			// convert N to NT where needed
			normalizedbids.push(`${b[0]}NT`);
		} else if (b == 'AP') {
			// convert AP to P, P, P
			normalizedbids.push('P');
			normalizedbids.push('P');
			normalizedbids.push('P');
		} else if (b === 'Pass') {
			normalizedbids.push('P');
		} else {
			normalizedbids.push(b);
		}
	});

	const auction = {
		value: matches[1],
		bids: normalizedbids,
	};

	return auction;
};

// },
// {
//   "type": "tag",
//   "name": "Play",
//   "value": "S",
//   "section": [
//     "H2   H3   H4   HA"
//   ],
//   "tokens": [
//     "H2",
//     "H3",
//     "H4",
//     "HA"
//   ]
// },
// [Play "S"]
// H2   H3   H4   HA
// DealParser.parsePlayTag = () => {};

module.exports = DealParser;
